import { inject } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, throwError } from 'rxjs';
import { environment } from '@app/env';
import { AuthStore } from '../store';

const AUTH_SKEY_HEADER = 'x-boemm-skey';

let sessionCheck$: Observable<boolean> | null = null;

function isSessionExpired(httpBackend: HttpBackend, authToken: string): Observable<boolean> {
  sessionCheck$ ??= new HttpClient(httpBackend)
    .get(`${environment.apiBaseUrl}/users/current-user`, {
      headers: new HttpHeaders({ [AUTH_SKEY_HEADER]: authToken }),
    })
    .pipe(
      map(() => false),
      catchError(
        (error: HttpErrorResponse) =>
          of(error.status === HttpStatusCode.Unauthorized || error.status === 0),
      ),
      finalize(() => (sessionCheck$ = null)),
      shareReplay(1),
    );

  return sessionCheck$;
}

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const authStore = inject(AuthStore);
  const httpBackend = inject(HttpBackend);
  const authToken = authStore.token();

  if (authToken) {
    req = req.clone({
      headers: req.headers.append(AUTH_SKEY_HEADER, authToken),
    });
  }

  return next(req).pipe(
    catchError(error => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status === HttpStatusCode.Unauthorized) {
        authStore.logout();
      } else if (error.status === 0 && authToken) {
        return isSessionExpired(httpBackend, authToken).pipe(
          switchMap(expired => {
            if (expired && authStore.token() === authToken) {
              authStore.logout();
            }
            return throwError(() => error);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
}
